// src/components/AdminComments.jsx
import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';

const AdminComments = ({ 
    featureRef, 
    organization, 
    isAdmin,
    className = "" 
}) => {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [error, setError] = useState(null);

    // Fetch comments
    useEffect(() => {
        if (!isAdmin || !organization?.id) return;
        
        const fetchComments = async () => {
            try {
                const response = await fetch(
                    `/api/admin/${organization.id}/${featureRef}/comments`, 
                    { credentials: 'include' }
                );
                
                if (!response.ok) {
                    throw new Error('Failed to fetch comments');
                }
                
                const data = await response.json();
                setComments(data);
            } catch (error) {
                console.error('Error fetching admin comments:', error);
                setError('Failed to load admin comments');
            }
        };

        fetchComments();
    }, [featureRef, organization?.id, isAdmin]);

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        try {
            const response = await fetch('/api/admin/comments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    feature_reference: featureRef,
                    organization_id: organization.id,
                    comment_text: newComment
                })
            });

            if (!response.ok) {
                throw new Error('Failed to add comment');
            }

            const comment = await response.json();
            setComments(prev => [comment, ...prev]);
            setNewComment('');
        } catch (error) {
            console.error('Error adding admin comment:', error);
            setError('Failed to add comment');
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            const response = await fetch(`/api/admin/comments/${commentId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to delete comment');
            }

            setComments(prev => prev.filter(comment => comment.id !== commentId));
        } catch (error) {
            console.error('Error deleting admin comment:', error);
            setError('Failed to delete comment');
        }
    };

    if (!isAdmin) return null;

    return (
        <div className={`space-y-4 ${className}`}>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="text-sm font-medium text-amber-900 mb-2">
                    Admin Comments
                </h4>

                {error && (
                    <div className="text-red-600 text-sm mb-2">{error}</div>
                )}

                <form onSubmit={handleAddComment} className="mb-4">
                    <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add an admin comment..."
                        className="w-full px-3 py-2 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        rows={2}
                    />
                    <div className="flex justify-end mt-2">
                        <button
                            type="submit"
                            disabled={!newComment.trim()}
                            className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Add Comment
                        </button>
                    </div>
                </form>

                <div className="space-y-3">
                    {comments.map(comment => (
                        <div 
                            key={comment.id} 
                            className="bg-white p-3 rounded-lg border border-amber-200"
                        >
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <span className="font-medium text-amber-900">
                                        {comment.user_name}
                                    </span>
                                    <span className="text-xs text-amber-700 ml-2">
                                        {new Date(comment.created_at).toLocaleString()}
                                    </span>
                                </div>
                                <button
                                    onClick={() => handleDeleteComment(comment.id)}
                                    className="text-amber-700 hover:text-amber-900"
                                    title="Delete comment"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            <p className="text-sm text-amber-800 whitespace-pre-wrap">
                                {comment.comment_text}
                            </p>
                        </div>
                    ))}
                </div>

                {comments.length === 0 && (
                    <p className="text-sm text-amber-700 text-center py-2">
                        No admin comments yet
                    </p>
                )}
            </div>
        </div>
    );
};

export default AdminComments;